import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { NurseryRoster, NurseryTeam } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

const DAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const when = (iso: string) => new Date(iso).toLocaleString('es', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });

/**
 * Turnos de cuidado: el líder (o co-líder, o la administración) elige qué cultos se cubren y asigna a
 * los integrantes. Quien está en el rol tiene las herramientas de la guardería solo ese día.
 */
export function RosterView({ churchId, team, version }: { churchId: string; team: NurseryTeam; version: number }) {
  const data = useAsync((signal) => api.get<NurseryRoster>(`/nursery/churches/${churchId}/roster`, signal), [churchId, version]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(key: string, action: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await action();
      data.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  if (data.status === 'loading') return <Loading />;
  const roster = data.data;
  const selected = roster.types.filter((t) => t.selected).map((t) => t.id);
  const toggleType = (id: string) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    void run(`type:${id}`, () => api.put(`/nursery/churches/${churchId}/roster/types`, { serviceTypeIds: next }));
  };

  return (
    <>
      <Card title="Mis turnos">
        {team.onDutyToday ? <Alert>Hoy te toca cuidar: tienes las herramientas de la guardería en la pestaña Salas.</Alert> : null}
        {team.myShifts.length === 0 ? (
          <p className="muted small">No tienes turnos próximos.</p>
        ) : (
          <ul className="list">
            {team.myShifts.map((s) => (
              <li key={s.serviceId}>
                <strong>{s.title}</strong> <span className="muted small">· {when(s.startsAt)}</span>
              </li>
            ))}
          </ul>
        )}
        {team.chatId ? (
          <Link className="btn btn--secondary" to={`/app/chats/${team.chatId}`}>
            Chat de Guardería
          </Link>
        ) : null}
      </Card>

      {roster.canManage ? (
        <Card title="¿Qué cultos se cubren?">
          <p className="small muted">Marca los tipos de culto en los que debe haber quien cuide a los niños.</p>
          {roster.types.length === 0 ? <p className="muted small">La iglesia aún no tiene tipos de culto.</p> : null}
          <div className="chips">
            {roster.types.map((t) => (
              <button
                key={t.id}
                type="button"
                aria-pressed={t.selected}
                className={`chip${t.selected ? ' chip--active' : ''}`}
                disabled={busy !== null}
                onClick={() => toggleType(t.id)}
              >
                {t.name}
                {t.weekday !== null ? ` · ${DAYS[t.weekday]}` : ''}
              </button>
            ))}
          </div>
        </Card>
      ) : null}

      {error ? <Alert>{error}</Alert> : null}
      {roster.services.length === 0 ? (
        <EmptyState title={selected.length === 0 ? 'Aún no se eligen los cultos del rol.' : 'No hay cultos próximos de esos tipos.'} />
      ) : null}
      {roster.services.map((sv) => {
        const free = roster.team.filter((p) => !sv.people.some((x) => x.personId === p.personId));
        return (
          <Card key={sv.id} title={sv.title} action={<span className="muted small">{when(sv.startsAt)}</span>}>
            {sv.people.length === 0 ? <p className="muted small">Nadie asignado.</p> : null}
            <ul className="list">
              {sv.people.map((p) => (
                <li key={p.personId} className="row">
                  <span>{p.name}</span>
                  {roster.canManage ? (
                    <Button
                      type="button"
                      variant="ghost"
                      busy={busy === `del:${sv.id}:${p.personId}`}
                      onClick={() => void run(`del:${sv.id}:${p.personId}`, () => api.delete(`/nursery/churches/${churchId}/roster/services/${sv.id}/people/${p.personId}`))}
                    >
                      Quitar
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
            {roster.canManage && free.length > 0 ? (
              <div className="field">
                <label htmlFor={`assign-${sv.id}`}>Asignar</label>
                <select
                  id={`assign-${sv.id}`}
                  value=""
                  disabled={busy !== null}
                  onChange={(e) => {
                    const personId = e.target.value;
                    if (personId) void run(`add:${sv.id}`, () => api.put(`/nursery/churches/${churchId}/roster/services/${sv.id}/people/${personId}`, {}));
                  }}
                >
                  <option value="">Elige a alguien del ministerio…</option>
                  {free.map((p) => (
                    <option key={p.personId} value={p.personId}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </Card>
        );
      })}
    </>
  );
}
